import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight, Youtube, Twitch, Instagram, Twitter, Music, Mail, Globe, MessageCircle, Video, Headphones } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Platform icon mapping
const PLATFORM_ICONS = {
  youtube: Youtube,
  twitch: Twitch,
  instagram: Instagram,
  twitter: Twitter,
  spotify: Music,
  tiktok: Video,
  discord: MessageCircle,
  soundcloud: Headphones,
  default: Globe
};

// Get or create visitor ID
const getVisitorId = () => {
  let visitorId = localStorage.getItem('kezzio_visitor_id');
  if (!visitorId) {
    visitorId = 'v_' + Math.random().toString(36).substring(2, 15);
    localStorage.setItem('kezzio_visitor_id', visitorId);
  }
  return visitorId;
};

// Track page view
const trackPageView = async (page) => {
  try {
    await fetch(`${API}/analytics/track?page=${encodeURIComponent(page)}&visitor_id=${getVisitorId()}`, {
      method: 'POST'
    });
  } catch (e) {
    // Silent fail - analytics shouldn't break the site
  }
};

export default function HomePage() {
  const [albums, setAlbums] = useState([]);
  const [currentAlbum, setCurrentAlbum] = useState(0);
  const [latestBlogs, setLatestBlogs] = useState([]);
  const [featuredVideo, setFeaturedVideo] = useState(null);
  const [news, setNews] = useState(null);
  const [comments, setComments] = useState([]);
  const [commentForm, setCommentForm] = useState({ name: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [socials, setSocials] = useState([]);
  const [homepageSettings, setHomepageSettings] = useState(null);

  useEffect(() => {
    fetchData();
    trackPageView('/home');
  }, []);

  const fetchData = async () => {
    try {
      const [albumsRes, blogsRes, videoRes, newsRes, commentsRes, socialsRes, homepageRes] = await Promise.all([
        fetch(`${API}/albums`),
        fetch(`${API}/blogs/latest?limit=4`),
        fetch(`${API}/videos/featured`),
        fetch(`${API}/news/latest`),
        fetch(`${API}/comments`),
        fetch(`${API}/socials`),
        fetch(`${API}/homepage`),
      ]);
      
      if (albumsRes.ok) setAlbums(await albumsRes.json());
      if (blogsRes.ok) setLatestBlogs(await blogsRes.json());
      if (videoRes.ok) {
        const video = await videoRes.json();
        if (video) setFeaturedVideo(video);
      }
      if (newsRes.ok) {
        const newsData = await newsRes.json();
        if (newsData) setNews(newsData);
      }
      if (commentsRes.ok) setComments(await commentsRes.json());
      if (socialsRes.ok) setSocials(await socialsRes.json());
      if (homepageRes.ok) setHomepageSettings(await homepageRes.json());
    } catch (e) {
      console.error("Error fetching data:", e);
    }
  };

  const nextAlbum = () => {
    setCurrentAlbum((prev) => (prev + 1) % albums.length);
  };

  const prevAlbum = () => {
    setCurrentAlbum((prev) => (prev - 1 + albums.length) % albums.length);
  };

  const submitComment = async (e) => {
    e.preventDefault();
    if (!commentForm.name.trim() || !commentForm.message.trim()) {
      toast.error("Please fill in both fields");
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/comments`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(commentForm),
      });
      if (res.ok) {
        toast.success("Comment posted!");
        setCommentForm({ name: "", message: "" });
        fetchData();
      }
    } catch (e) {
      toast.error("Error posting comment");
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  // Helper to get section settings
  const getSection = (key) => {
    if (!homepageSettings?.sections) return { visible: true, title: '', colors: {} };
    return homepageSettings.sections.find(s => s.key === key) || { visible: true, title: '', colors: {} };
  };

  // Helper to build section styles
  const getSectionStyle = (colors) => {
    const style = {};
    if (colors?.bg) style.backgroundColor = colors.bg;
    if (colors?.border) style.borderColor = colors.border;
    return style;
  };

  const getTitleStyle = (colors) => {
    const style = {};
    if (colors?.accent) style.color = colors.accent;
    if (colors?.bg) style.backgroundColor = colors.bg;
    if (colors?.border) style.borderColor = colors.border;
    return style;
  };

  const getTextStyle = (colors) => {
    const style = {};
    if (colors?.text) style.color = colors.text;
    return style;
  };

  const currentAlbumData = albums[currentAlbum];

  // Get sorted visible sections for each column
  // Badges are rendered separately after the holly button
  const leftSections = ['latest_video', 'blogs', 'socials'];
  const rightSections = ['music', 'news', 'comments'];

  const sortedLeftSections = leftSections
    .map(key => ({ key, ...getSection(key) }))
    .filter(s => s.visible)
    .sort((a, b) => a.order - b.order);

  const sortedRightSections = rightSections
    .map(key => ({ key, ...getSection(key) }))
    .filter(s => s.visible)
    .sort((a, b) => a.order - b.order);
  
  // Get badges section separately
  const badgesSection = getSection('badges');

  // Render section components
  const renderSection = (sectionData) => {
    const { key, title, colors } = sectionData;

    switch (key) {
      case 'latest_video':
        return (
          <div 
            key={key}
            className="myspace-section" 
            data-testid="latest-video-section"
            style={getSectionStyle(colors)}
          >
            <div className="myspace-section-title" style={getTitleStyle(colors)}>{title || "Latest Video"}</div>
            <div className="myspace-section-content" style={getTextStyle(colors)}>
              {featuredVideo ? (
                <div className="video-embed-2006">
                  <iframe
                    src={`https://www.youtube.com/embed/${featuredVideo.youtube_id}`}
                    title={featuredVideo.title}
                    width="100%"
                    height="150"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                </div>
              ) : (
                <div className="opacity-80 text-[11px]" style={getTextStyle(colors)}>No video yet</div>
              )}
            </div>
          </div>
        );

      case 'blogs':
        return (
          <div 
            key={key}
            className="myspace-section" 
            data-testid="latest-blogs-section"
            style={getSectionStyle(colors)}
          >
            <div className="myspace-section-title" style={getTitleStyle(colors)}>{title || "Latest Blog Entries"}</div>
            <div className="myspace-section-content" style={getTextStyle(colors)}>
              {latestBlogs.length > 0 ? (
                <ul className="link-list">
                  {latestBlogs.map((blog, index) => (
                    <li key={blog.id}>
                      <Link 
                        to={`/blog/${blog.id}`}
                        data-testid={`blog-entry-${index}`}
                        style={colors?.accent ? { color: colors.accent } : {}}
                      >
                        {blog.title}
                      </Link>
                      <div className="text-[10px] opacity-60" style={getTextStyle(colors)}>{formatDate(blog.created_at)}</div>
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="opacity-80 text-[11px]" style={getTextStyle(colors)}>No blog entries yet</div>
              )}
            </div>
          </div>
        );

      case 'socials':
        return (
          <div 
            key={key}
            className="myspace-section" 
            data-testid="socials-section"
            style={getSectionStyle(colors)}
          >
            <div className="myspace-section-title" style={getTitleStyle(colors)}>{title || "Socials"}</div>
            <div className="myspace-section-content" style={getTextStyle(colors)}>
              {socials.map((social) => {
                const IconComponent = PLATFORM_ICONS[social.platform] || PLATFORM_ICONS.default;
                return (
                  <a
                    key={social.id}
                    href={social.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="social-icon-2006 block no-underline"
                    data-testid={`social-${social.platform}`}
                    style={colors?.accent ? { color: colors.accent } : {}}
                  >
                    <IconComponent size={14} />
                    <span>{social.display_name}</span>
                  </a>
                );
              })}
            </div>
          </div>
        );

      case 'music':
        return (
          <div 
            key={key}
            className="myspace-section" 
            data-testid="music-section"
            style={getSectionStyle(colors)}
          >
            <div className="myspace-section-title" style={getTitleStyle(colors)}>{title || ":: Music :: Choose Your Album ::"}</div>
            <div className="myspace-section-content" style={getTextStyle(colors)}>
              {albums.length > 0 && currentAlbumData ? (
                <div className="flex flex-col items-center py-4">
                  <div className="flex items-center justify-center gap-2">
                    {albums.length > 1 && (
                      <a
                        href={albums[(currentAlbum - 1 + albums.length) % albums.length].bandcamp_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block opacity-40 hover:opacity-60 transition-opacity"
                        data-testid="album-prev-preview"
                      >
                        <img
                          src={albums[(currentAlbum - 1 + albums.length) % albums.length].cover_url}
                          alt={albums[(currentAlbum - 1 + albums.length) % albums.length].title}
                          className="album-thumb-2006 w-20 h-20 object-cover"
                        />
                      </a>
                    )}
                    <button 
                      onClick={prevAlbum}
                      className="arrow-btn-2006"
                      data-testid="album-prev"
                      disabled={albums.length <= 1}
                      style={colors?.accent ? { borderColor: colors.accent, color: colors.accent } : {}}
                    >
                      <ChevronLeft size={18} />
                    </button>
                    <a
                      href={currentAlbumData.bandcamp_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block text-center mx-2"
                      data-testid="album-center"
                    >
                      <img
                        src={currentAlbumData.cover_url}
                        alt={currentAlbumData.title}
                        className="album-thumb-2006 w-44 h-44 object-cover"
                      />
                    </a>
                    <button 
                      onClick={nextAlbum}
                      className="arrow-btn-2006"
                      data-testid="album-next"
                      disabled={albums.length <= 1}
                      style={colors?.accent ? { borderColor: colors.accent, color: colors.accent } : {}}
                    >
                      <ChevronRight size={18} />
                    </button>
                    {albums.length > 1 && (
                      <a
                        href={albums[(currentAlbum + 1) % albums.length].bandcamp_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block opacity-40 hover:opacity-60 transition-opacity"
                        data-testid="album-next-preview"
                      >
                        <img
                          src={albums[(currentAlbum + 1) % albums.length].cover_url}
                          alt={albums[(currentAlbum + 1) % albums.length].title}
                          className="album-thumb-2006 w-20 h-20 object-cover"
                        />
                      </a>
                    )}
                  </div>
                  <div className="text-[12px] mt-3 font-bold" style={colors?.accent ? { color: colors.accent } : { color: 'var(--accent-orange)' }}>
                    {currentAlbumData.title}
                  </div>
                  <div className="text-[9px] opacity-50 mt-2" style={getTextStyle(colors)}>
                    Click album cover to listen on Bandcamp
                  </div>
                </div>
              ) : (
                <div className="text-center opacity-80 text-[11px] py-4" style={getTextStyle(colors)}>
                  No albums yet
                </div>
              )}
            </div>
          </div>
        );

      case 'news':
        return (
          <div 
            key={key}
            className="myspace-section" 
            data-testid="news-section"
            style={getSectionStyle(colors)}
          >
            <div className="myspace-section-title" style={getTitleStyle(colors)}>{title || ":: News & Updates ::"}</div>
            <div className="myspace-section-content" style={getTextStyle(colors)}>
              {news ? (
                <div className="news-box-2006" style={colors?.border ? { borderColor: colors.border } : {}}>
                  <div className="news-date" style={colors?.accent ? { color: colors.accent } : {}}>Posted: {formatDate(news.created_at)}</div>
                  <div className="text-[11px] opacity-80 leading-relaxed" style={getTextStyle(colors)}>
                    {news.content}
                  </div>
                </div>
              ) : (
                <div className="opacity-80 text-[11px]" style={getTextStyle(colors)}>No news yet. Check back soon!</div>
              )}
            </div>
          </div>
        );

      case 'comments':
        return (
          <div 
            key={key}
            className="myspace-section" 
            data-testid="comments-section"
            style={getSectionStyle(colors)}
          >
            <div className="myspace-section-title" style={getTitleStyle(colors)}>{title || ":: Leave a Comment ::"}</div>
            <div className="myspace-section-content" style={getTextStyle(colors)}>
              <form onSubmit={submitComment} className="mb-4">
                <div className="mb-2">
                  <input
                    type="text"
                    placeholder="Your Name"
                    value={commentForm.name}
                    onChange={(e) => setCommentForm({...commentForm, name: e.target.value})}
                    className="input-2006 w-full"
                    data-testid="comment-name"
                    style={colors?.border ? { borderColor: colors.border } : {}}
                  />
                </div>
                <div className="mb-2">
                  <textarea
                    placeholder="Your Message..."
                    value={commentForm.message}
                    onChange={(e) => setCommentForm({...commentForm, message: e.target.value})}
                    className="textarea-2006 w-full h-20"
                    data-testid="comment-message"
                    style={colors?.border ? { borderColor: colors.border } : {}}
                  />
                </div>
                <button 
                  type="submit" 
                  className="btn-2006 btn-2006-orange"
                  disabled={submitting}
                  data-testid="comment-submit"
                  style={colors?.accent ? { backgroundColor: colors.accent, borderColor: colors.accent } : {}}
                >
                  {submitting ? "Posting..." : "Post Comment"}
                </button>
              </form>
              <div className="border-t pt-3" style={colors?.border ? { borderColor: colors.border } : { borderColor: '#1a4a7a' }}>
                <div className="text-[10px] mb-2 font-bold" style={colors?.accent ? { color: colors.accent } : { color: 'var(--accent-orange)' }}>
                  {comments.length} Comment{comments.length !== 1 ? 's' : ''}
                </div>
                <div className="max-h-[280px] overflow-y-auto pr-1" style={{ scrollbarWidth: 'thin' }}>
                  {comments.slice(0, 10).map((comment) => (
                    <div key={comment.id} className="comment-box-2006 mb-2" style={colors?.border ? { borderColor: colors.border } : {}}>
                      <div className="flex justify-between items-start">
                        <span className="font-bold text-[11px]" style={colors?.accent ? { color: colors.accent } : { color: 'var(--accent-cyan)' }}>{comment.name}</span>
                        <span className="text-[9px] opacity-50" style={getTextStyle(colors)}>{formatDate(comment.created_at)}</span>
                      </div>
                      <div className="text-[11px] opacity-80 mt-1" style={getTextStyle(colors)}>{comment.message}</div>
                    </div>
                  ))}
                  {comments.length === 0 && (
                    <div className="text-[10px] opacity-50" style={getTextStyle(colors)}>No comments yet. Be the first!</div>
                  )}
                </div>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="page-wrapper" data-testid="homepage">
      <div className="myspace-container">
        {/* Header */}
        <div className="myspace-header">
          <h3>kezzio's space</h3>
        </div>

        {/* Main content table */}
        <table className="profile-table">
          <tbody>
            <tr>
              {/* LEFT COLUMN */}
              <td className="left-column">
                {sortedLeftSections.map(section => renderSection(section))}

                {/* Secret Holly Button - barely visible */}
                <Link
                  to="/holly"
                  className="holly-secret-button"
                  style={{
                    display: 'block',
                    width: '40px',
                    height: '40px',
                    margin: '20px auto 10px auto',
                    opacity: 0.08,
                    filter: 'grayscale(100%) contrast(50%)',
                    backgroundImage: 'url(/holly.png)',
                    backgroundSize: 'contain',
                    backgroundRepeat: 'no-repeat',
                    backgroundPosition: 'center',
                    cursor: 'default',
                    transition: 'opacity 0.3s',
                  }}
                  onMouseEnter={(e) => e.target.style.opacity = '0.15'}
                  onMouseLeave={(e) => e.target.style.opacity = '0.08'}
                  data-testid="holly-secret"
                />

                {/* Web Badges - below holly button */}
                {badgesSection.visible && homepageSettings?.badges?.length > 0 && (
                  <div 
                    className="flex flex-wrap gap-2 mt-2 mb-4 pl-2"
                    data-testid="badges-section"
                  >
                    {homepageSettings.badges.map((badge, idx) => (
                      <a 
                        key={idx}
                        href={badge.url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                      >
                        <img 
                          src={badge.image_url} 
                          alt={badge.alt} 
                          loading="lazy"
                          className="block"
                          style={{ imageRendering: 'pixelated' }}
                        />
                      </a>
                    ))}
                  </div>
                )}
              </td>

              {/* RIGHT COLUMN */}
              <td className="right-column">
                {sortedRightSections.map(section => renderSection(section))}
              </td>
            </tr>
          </tbody>
        </table>

        {/* Footer with contact email and hidden admin link */}
        <div className="text-center text-[10px] py-3 border-t border-[#1a4a7a] relative">
          <div className="flex items-center justify-center gap-1 text-[var(--text-white)] opacity-60">
            <Mail size={12} className="text-[var(--accent-orange)]" />
            <a href="mailto:kelsonbusinessemail@gmail.com" className="text-[var(--text-link)] hover:text-[var(--accent-cyan)]">
              kelsonbusinessemail@gmail.com
            </a>
            <span className="mx-2">|</span>
            <a href="https://www.youtube.com/@kezziofr" className="text-[var(--text-link)]">YouTube</a>
            <span className="mx-1">|</span>
            <a href="https://kezzio.bandcamp.com" className="text-[var(--text-link)]">Bandcamp</a>
          </div>
          <Link 
            to="/admin" 
            className="absolute right-4 top-1/2 -translate-y-1/2 text-[#333] hover:text-[#666] cursor-pointer p-2"
            data-testid="hidden-admin-link"
          >•</Link>
        </div>
      </div>
    </div>
  );
}
